import { useEffect, useRef } from 'react';
import { track, EVENTS } from '../lib/mixpanel';

/**
 * Detects potential devtools usage via two heuristics:
 * 1. Window resize — large delta between outer and inner dimensions suggests devtools panel
 * 2. Common devtools keyboard shortcuts (F12, Ctrl+Shift+I/J/C, Cmd+Opt+I)
 *
 * Calls `onDetected` with the method that triggered detection.
 * The caller is responsible for debouncing to prevent log spam.
 */
export function useDevToolsDetection(
  onDetected: (method: 'resize' | 'keyboard') => void,
): void {
  // Keep a stable ref so effects don't re-run when callback identity changes
  const onDetectedRef = useRef(onDetected);
  useEffect(() => {
    onDetectedRef.current = onDetected;
  });

  // Resize heuristic
  useEffect(() => {
    const THRESHOLD = 160;
    const check = () => {
      if (
        window.outerWidth - window.innerWidth > THRESHOLD ||
        window.outerHeight - window.innerHeight > THRESHOLD
      ) {
        track(EVENTS.DEVTOOLS_DETECTED, { method: 'resize' });
        onDetectedRef.current('resize');
      }
    };
    window.addEventListener('resize', check, { passive: true });
    return () => window.removeEventListener('resize', check);
  }, []);

  // Keyboard shortcut detection
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      const isDevToolsKey =
        e.key === 'F12' ||
        (e.ctrlKey && e.shiftKey && ['I', 'J', 'C'].includes(e.key.toUpperCase())) ||
        (e.metaKey && e.altKey && e.key.toUpperCase() === 'I');

      if (isDevToolsKey) {
        e.preventDefault();
        track(EVENTS.DEVTOOLS_DETECTED, { method: 'keyboard' });
        onDetectedRef.current('keyboard');
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, []);
}
